import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Puts a newly navigated page where it belongs — the top, or the fragment that was asked for.
 *
 * ## Three separate bugs live here
 *
 * **A browser restores scroll position on a real page load; a single-page app does not load a
 * page.** It swaps the component tree and leaves the scroll offset untouched. Click "Privacy
 * Policy" from the footer and the privacy page renders correctly, then shows you its footer,
 * because you were three thousand pixels down when you clicked.
 *
 * **React Router does not perform fragment scrolling.** `<Link to="/#pricing">` pushes history
 * state and stops; the browser's native hash handling never runs. The header's section links work
 * only by accident of being plain `<a href="#features">` anchors. Anything using `<Link>` needs
 * this component.
 *
 * **`useLocation` does not report a hash-only change.** Clicking "Pricing" in the footer while
 * already on the landing page updates the URL and never re-runs the location effect — verified by
 * instrumenting `window.scrollTo` and watching it record nothing. The browser fires `hashchange`
 * for precisely that case, so it is a required second trigger rather than a redundant one.
 *
 * ## Instant across pages, smooth within one
 *
 * `scroll-behavior: smooth` is set globally so in-page anchors glide. Honouring it on a cross-page
 * jump would animate a scroll through the whole of the page you just left — motion nobody asked
 * for, on content they have already abandoned.
 */

/**
 * How far above the target to stop, so the sticky header does not cover it.
 *
 * Matches the `scroll-margin-top` in globals.css, which covers the anchors the browser scrolls
 * natively. Both exist because two different mechanisms scroll this site.
 */
const HEADER_OFFSET = 96;

/**
 * How long to keep looking for a fragment target before giving up, in ~16ms ticks.
 *
 * Four seconds, which is generous on purpose. `/` renders through HomeRoute, which
 * resolves the session before it decides to show the landing page at all, and a cold development
 * build compiles the landing module on demand. One second was measured to be too short: the retry
 * expired, the fallback fired, and the visitor landed at the top of the page — indistinguishable
 * from the bug this exists to fix.
 *
 * A long window is harmless because giving up now does nothing at all.
 */
const MAX_TICKS = 240;

/**
 * Deliberately `setTimeout` rather than `requestAnimationFrame`.
 *
 * rAF only fires when the page is compositing frames. A link opened in a background tab, a page
 * behind another window, or an embedded webview that is not being painted will never run the
 * callback — so the scroll simply never happens and the visitor lands wherever they were. Measured
 * exactly that: the no-hash path, which calls `scrollTo` directly, worked while the hash path
 * behind rAF did nothing at all. A timer is not frame-aligned, which costs nothing here because
 * the scroll itself is animated by the browser.
 */
const TICK_MS = 16;

/** Scrolls to `hash`, or to the top when there is none. Returns a cleanup for its retry loop. */
function applyScroll(hash: string, changedPage: boolean): () => void {
  if (hash === '') {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    return () => {};
  }

  // `decodeURIComponent` because a fragment may be percent-encoded in the URL bar while the
  // element's id is not.
  const id = decodeURIComponent(hash.slice(1));

  let elapsed = 0;
  let timer = 0;
  let verify = 0;

  const scroll = () => {
    const target = document.getElementById(id);

    if (target === null) {
      elapsed += 1;

      if (elapsed < MAX_TICKS) {
        timer = window.setTimeout(scroll, TICK_MS);
      }

      // Deliberately nothing when the window expires. An earlier version scrolled to the top here,
      // reasoning that a fragment matching nothing should not strand the visitor mid-page — but on
      // a fresh load the browser is already at the top, so the only thing that fallback ever
      // achieved was undoing a scroll that was about to become possible.
      return;
    }

    // Deliberately not `scrollIntoView`. It would respect `scroll-margin-top`, which is neater, but
    // it silently does nothing in several embedded and headless contexts — including the one this
    // was verified in, where the target stayed thousands of pixels off-screen while the call
    // reported success. `window.scrollTo` with a computed offset works everywhere and is testable.
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET);
    const before = window.scrollY;

    window.scrollTo({
      top,
      left: 0,
      behavior: changedPage ? 'instant' : 'smooth',
    });

    if (changedPage) {
      return;
    }

    /*
     * Smooth scrolling is accepted and then ignored in more places than it is honoured.
     *
     * Measured in an embedded browser on this project: `window.scrollTo({behavior: 'smooth'})`
     * returned normally and moved nothing at all, while the same call with `'instant'` worked --
     * with `prefers-reduced-motion` reporting false and the computed `scroll-behavior` reporting
     * `smooth`. Nothing in the API reports the failure, so every in-page anchor on the landing
     * page silently did nothing: the URL changed, the header highlighted, and the visitor stayed
     * where they were.
     *
     * So the animation is attempted and then checked. If the page has not begun to move by the
     * time a smooth scroll would obviously have started, the jump is repeated without it. A
     * scroll that lands abruptly is worth far more than one that does not land.
     */
    verify = window.setTimeout(() => {
      const moved = Math.abs(window.scrollY - before) >= 2;
      const shouldHaveMoved = Math.abs(top - before) >= 2;

      if (!moved && shouldHaveMoved) {
        window.scrollTo({ top, left: 0, behavior: 'instant' });
      }
    }, 120);
  };

  timer = window.setTimeout(scroll, 0);

  return () => {
    window.clearTimeout(timer);
    window.clearTimeout(verify);
  };
}

export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    const changedPage = previousPathname.current !== pathname;
    previousPathname.current = pathname;

    return applyScroll(hash, changedPage);
  }, [pathname, hash]);

  useEffect(() => {
    const onHashChange = () => applyScroll(window.location.hash, false);

    window.addEventListener('hashchange', onHashChange);

    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return null;
}
